import React, { useMemo, useRef, useState } from 'react';
import type { BinColor, SoppelLevel } from '../../data/schemas';
import { BIN_HEX } from './colors';
import { playDumpSound, playWobbleSound } from './sounds';

export interface RecyclePlayProps {
  level: SoppelLevel;
  onActivity: () => void;
  onComplete: () => void;
}

type Bag = { id: string; color: BinColor };

export const RecyclePlay: React.FC<RecyclePlayProps> = ({ level, onActivity, onComplete }) => {
  const recycling = level.recycling;
  const bags = useMemo(() => {
    if (!recycling) return [] as Bag[];
    const list: Bag[] = [];
    recycling.containers.forEach((color) => {
      for (let i = 0; i < recycling.bagsPerColor; i += 1) {
        list.push({ id: `bag-${color}-${i}`, color });
      }
    });
    // Shuffle lightly so colors aren't strictly sequential.
    for (let i = list.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
  }, [recycling]);

  const [bagIndex, setBagIndex] = useState(0);
  const [wobbleId, setWobbleId] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const origin = useRef({ x: 0, y: 0 });
  const bagRef = useRef<HTMLDivElement>(null);

  if (!recycling) {
    return (
      <div className="flex h-full items-center justify-center text-white font-display text-2xl">
        Mangler recycling-data
      </div>
    );
  }

  const current = bags[bagIndex];
  const done = bagIndex >= bags.length;

  const finishBag = () => {
    playDumpSound();
    const next = bagIndex + 1;
    setBagIndex(next);
    setDragging(false);
    setPos({ x: 0, y: 0 });
    if (next >= bags.length) onComplete();
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!current || done) return;
    onActivity();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    origin.current = { x: e.clientX, y: e.clientY };
    setPos({ x: 0, y: 0 });
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging) return;
    setPos({ x: e.clientX - origin.current.x, y: e.clientY - origin.current.y });
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (!dragging || !current) return;
    setDragging(false);
    const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
    const container = el?.closest('[data-container-color]') as HTMLElement | null;
    const color = container?.dataset.containerColor as BinColor | undefined;
    if (color && color === current.color) {
      finishBag();
      return;
    }
    if (color && color !== current.color) {
      playWobbleSound();
      setWobbleId(color);
      window.setTimeout(() => setWobbleId(null), 400);
    }
    setPos({ x: 0, y: 0 });
  };

  return (
    <div
      className="absolute inset-0 flex h-full min-h-[100dvh] w-full flex-col items-center justify-between bg-gradient-to-b from-[#B0C4DE] to-[#A0A0A0] p-4 pt-24 pb-8"
      data-name="recycle-play"
    >
      <p className="font-display text-2xl font-bold text-white drop-shadow text-center">
        Dra posen til samme farge
      </p>

      <div className="flex flex-wrap justify-center gap-4 my-6">
        {recycling.containers.map((color) => (
          <div
            key={color}
            data-container-color={color}
            data-name={`container-${color}`}
            className={`flex min-h-[120px] min-w-[120px] items-center justify-center rounded-3xl border-4 border-white/70 shadow-lg ${
              wobbleId === color ? 'animate-[enklespill-wobble_450ms_ease]' : ''
            }`}
            style={{ backgroundColor: BIN_HEX[color] }}
          >
            <span className="sr-only">{color}</span>
          </div>
        ))}
      </div>

      <div className="relative flex h-40 w-full items-center justify-center">
        {!done && current ? (
          <div
            ref={bagRef}
            data-name={`bag-${current.color}`}
            className="absolute flex min-h-[96px] min-w-[96px] cursor-grab items-center justify-center rounded-2xl shadow-xl active:cursor-grabbing touch-none select-none"
            style={{
              backgroundColor: BIN_HEX[current.color],
              transform: `translate(${pos.x}px, ${pos.y}px)`,
              zIndex: 5,
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={() => {
              setDragging(false);
              setPos({ x: 0, y: 0 });
            }}
          >
            <span className="font-display text-xl font-bold text-white">Pose</span>
          </div>
        ) : (
          <p className="font-display text-3xl text-white">Bra jobbet!</p>
        )}
      </div>

      <p className="font-body text-white/80">
        {Math.min(bagIndex, bags.length)} / {bags.length}
      </p>
    </div>
  );
};

export default RecyclePlay;
